import React, { useEffect, useState, useRef, Component } from 'react';
import { Zap, Shield, Check, Star, Clock, Award, TrendingUp, AlertTriangle, BookOpen, Video, CircuitBoard, DollarSign, Brain, Target, CreditCard, Download, Headphones, ChevronDown, ChevronUp, Lightbulb, Wrench, Monitor, ThumbsUp } from 'lucide-react';
import { useMetaPixel } from '../hooks/useMetaPixel';
import { usePurchaseNotifications } from '../hooks/usePurchaseNotifications';
import { PurchaseNotification } from '../components/PurchaseNotification';
// Color constants
const COPPER = '#B87333'; // Kept as fallback but mostly replaced
const NAVY = '#0C1E3A'; // Kept as fallback but mostly replaced
const NEON = '#FFFF00';
const CHARCOAL = '#1A1A1A';
const DARK = '#111111';
// FAQ Component
function FAQItem({
  question,
  answer
}: {
  question: string;
  answer: string;
}) {
  const [isOpen, setIsOpen] = useState(false);
  return <div className="border rounded-lg mb-3 overflow-hidden transition-colors" style={{
    background: CHARCOAL,
    borderColor: isOpen ? NEON : 'rgba(255,255,0,0.1)'
  }} data-id="element-30">
      <button onClick={() => setIsOpen(!isOpen)} className="w-full px-6 py-4 text-left flex justify-between items-center hover:bg-white/5 transition-colors" data-id="element-31">
        <span className="font-medium text-white pr-4" style={{
        fontFamily: "'Montserrat', sans-serif"
      }} data-id="element-32">
          {question}
        </span>
        {isOpen ? <ChevronUp className="w-5 h-5 flex-shrink-0" style={{
        color: NEON
      }} data-id="element-33" /> : <ChevronDown className="w-5 h-5 text-gray-600 flex-shrink-0" data-id="element-34" />}
      </button>
      {isOpen && <div className="px-6 py-4 border-t" style={{
      background: DARK,
      borderColor: 'rgba(255,255,0,0.1)'
    }} data-id="element-35">
          <p className="text-gray-400 leading-relaxed" data-id="element-36">{answer}</p>
        </div>}
    </div>;
}
// Dynamic Stats Hook
function useDynamicStats() {
  const [viewers, setViewers] = useState(47);
  const [spots, setSpots] = useState(9);
  useEffect(() => {
    const viewerInterval = setInterval(() => {
      setViewers(prev => {
        const change = Math.floor(Math.random() * 5) - 2;
        return Math.max(40, Math.min(55, prev + change));
      });
    }, Math.random() * 5000 + 8000);
    const spotInterval = setInterval(() => {
      setSpots(prev => prev > 3 && Math.random() > 0.65 ? prev - 1 : prev);
    }, 40000);
    return () => {
      clearInterval(viewerInterval);
      clearInterval(spotInterval);
    };
  }, []);
  return {
    viewers,
    spots
  };
}
// Review Form Component
function ReviewForm({
  onSubmitReview
}: {
  onSubmitReview: (review: {
    name: string;
    text: string;
    stars: number;
  }) => void;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [name, setName] = useState('');
  const [comment, setComment] = useState('');
  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [showSuccess, setShowSuccess] = useState(false);
  const [error, setError] = useState('');
  const handleSubmit = () => {
    if (!name.trim()) {
      setError('Por favor ingresá tu nombre');
      return;
    }
    if (rating === 0) {
      setError('Por favor seleccioná una calificación');
      return;
    }
    if (!comment.trim()) {
      setError('Por favor escribí un comentario');
      return;
    }
    setError('');
    setShowSuccess(true);
    onSubmitReview({
      name: name.trim(),
      text: comment.trim(),
      stars: rating
    });
    setName('');
    setComment('');
    setRating(0);
    setTimeout(() => {
      setShowSuccess(false);
      setIsOpen(false);
    }, 3000);
  };
  if (!isOpen) {
    return <div className="text-center mt-8" data-id="element-37">
        <button onClick={() => setIsOpen(true)} className="text-gray-400 hover:text-white text-sm font-medium underline transition-colors" style={{
        fontFamily: "'Roboto', sans-serif"
      }} data-id="element-38">
          ¿Compraste? Dejá tu reseña
        </button>
      </div>;
  }
  return <div className="border rounded-xl p-6 mt-8 max-w-2xl mx-auto shadow-sm relative animate-fade-in-up" style={{
    background: CHARCOAL,
    borderColor: 'rgba(255,255,0,0.2)'
  }} data-id="element-39">
      {showSuccess && <div className="absolute top-4 right-4 bg-green-600 text-white px-4 py-2 rounded-lg shadow-lg text-sm font-medium z-10" data-id="element-40">
          <p data-id="element-41">¡Gracias por tu reseña!</p>
        </div>}
      <div className="flex justify-between items-center mb-4" data-id="element-42">
        <h4 className="font-bold text-white flex items-center gap-2" data-id="element-43">
          <MessageSquare className="w-5 h-5" style={{
          color: NEON
        }} data-id="element-44" />
          Dejá tu reseña
        </h4>
        <button onClick={() => setIsOpen(false)} className="text-gray-500 hover:text-gray-300" data-id="element-45">
          <X className="w-5 h-5" data-id="element-46" />
        </button>
      </div>
      <div className="space-y-4" data-id="element-47">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-48">
          <input type="text" placeholder="Tu nombre" value={name} onChange={e => setName(e.target.value)} className="w-full px-4 py-2 border rounded-lg focus:ring-2 outline-none transition-all text-white placeholder-gray-500" style={{
          background: DARK,
          borderColor: 'rgba(255,255,255,0.1)',
          '--tw-ring-color': NEON
        } as React.CSSProperties} data-id="element-49" />
          <div className="flex items-center gap-1 px-4 py-2 border rounded-lg" style={{
          background: DARK,
          borderColor: 'rgba(255,255,255,0.1)'
        }} data-id="element-50">
            {[1, 2, 3, 4, 5].map(s => <Star key={s} className={`w-5 h-5 cursor-pointer transition-colors ${s <= (hoverRating || rating) ? 'fill-amber-500 text-amber-500' : 'text-gray-600'}`} onMouseEnter={() => setHoverRating(s)} onMouseLeave={() => setHoverRating(0)} onClick={() => setRating(s)} data-id="element-51" />)}
          </div>
        </div>
        <textarea placeholder="Contanos tu experiencia..." rows={3} value={comment} onChange={e => setComment(e.target.value)} className="w-full px-4 py-2 border rounded-lg focus:ring-2 outline-none transition-all resize-none text-white placeholder-gray-500" style={{
        background: DARK,
        borderColor: 'rgba(255,255,255,0.1)',
        '--tw-ring-color': NEON
      } as React.CSSProperties} data-id="element-52" />
        {error && <p className="text-red-400 text-sm" data-id="element-53">{error}</p>}
        <button onClick={handleSubmit} className="text-black font-bold py-2 px-6 rounded-lg transition-colors text-sm hover:brightness-110" style={{
        background: NEON
      }} data-id="element-54">
          Enviar reseña
        </button>
      </div>
    </div>;
}
// Testimonial Item with Like functionality
function TestimonialItem({
  t
}: {
  t: {
    name: string;
    msg: string;
    time: string;
    verified: boolean;
  };
}) {
  const [likes, setLikes] = useState(Math.floor(Math.random() * 100) + 20);
  const [liked, setLiked] = useState(false);
  const handleLike = () => {
    if (liked) {
      setLikes(prev => prev - 1);
      setLiked(false);
    } else {
      setLikes(prev => prev + 1);
      setLiked(true);
    }
  };
  return <div className="flex items-start gap-3" data-id="element-55">
      <div className="w-9 h-9 bg-green-600 rounded-full flex items-center justify-center text-white font-bold text-sm flex-shrink-0" data-id="element-56">
        {t.name.charAt(0)}
      </div>
      <div className="flex-grow" data-id="element-57">
        <div className="flex items-center gap-2 mb-1" data-id="element-58">
          <span className="font-bold text-sm" style={{
          color: '#FFD700',
          fontFamily: "'Inter', sans-serif",
          letterSpacing: '-0.02em'
        }} data-id="element-59">
            {t.name}
          </span>
          {t.verified && <span className="text-[9px] px-1.5 py-0.5 rounded-full font-bold uppercase" style={{
          background: 'rgba(255, 215, 0, 0.1)',
          color: '#FFD700'
        }} data-id="element-60">
              ✓ Verificado
            </span>}
        </div>
        <div className="chat-bubble rounded-2xl rounded-tl-none p-3 inline-block" style={{
        background: CHARCOAL,
        border: '1px solid rgba(255,255,255,0.05)'
      }} data-id="element-61">
          <p className="text-sm text-gray-300 leading-relaxed" data-id="element-62">{t.msg}</p>
          <div className="flex justify-between items-center mt-2 gap-4" data-id="element-63">
            <p className="text-[10px] text-gray-500" data-id="element-64">{t.time}</p>
            <button onClick={handleLike} className={`flex items-center gap-1 text-[10px] font-medium transition-colors ${liked ? '' : 'text-gray-500 hover:text-gray-300'}`} style={{
            color: liked ? NEON : undefined
          }} data-id="element-65">
              <ThumbsUp className={`w-3 h-3 ${liked ? 'fill-current' : ''}`} data-id="element-66" />
              {likes}
            </button>
          </div>
        </div>
      </div>
    </div>;
}
export function LandingPage() {
  const {
    trackEvent
  } = useMetaPixel();
  const {
    notification,
    isVisible
  } = usePurchaseNotifications();
  const {
    viewers,
    spots
  } = useDynamicStats();
  const [headerVisible, setHeaderVisible] = useState(false);
  const [problemRealVisible, setProblemRealVisible] = useState(false);
  const [decisionVisible, setDecisionVisible] = useState(false);
  const [benefitsVisible, setBenefitsVisible] = useState(false);
  const [postHeroVisible, setPostHeroVisible] = useState(false);
  const [visibleTestimonials, setVisibleTestimonials] = useState(5);
  const [activeBonus, setActiveBonus] = useState(0);
  const bonusScrollRef = useRef<HTMLDivElement>(null);
  // Dynamic counter: 0 → 3,250 fast, then slow drip
  const [counterValue, setCounterValue] = useState(0);
  const counterIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  useEffect(() => {
    const target = 3250;
    const duration = 1500;
    const startTime = Date.now();
    const animateCounter = () => {
      const elapsed = Date.now() - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      const current = Math.floor(eased * target);
      setCounterValue(current);
      if (progress < 1) {
        requestAnimationFrame(animateCounter);
      } else {
        setCounterValue(target);
        counterIntervalRef.current = setInterval(() => {
          setCounterValue(prev => prev + 1);
        }, Math.random() * 15000 + 30000);
      }
    };
    requestAnimationFrame(animateCounter);
    return () => {
      if (counterIntervalRef.current) clearInterval(counterIntervalRef.current);
    };
  }, []);
  // Reviews pool - Electrical specific
  const allChatReviews = [{
    name: 'Javier M.',
    msg: 'La verdad 10 puntos. Antes me daba un poco de julepe meter mano en tableros grandes, pero con los planos que trae el manual vas a la segura. Muy bien explicado todo.',
    time: '14:32',
    verified: true
  }, {
    name: 'Gustavo R.',
    msg: 'Lo compré por la parte de medidores porque me salió un laburo de un pilar monofásico y estaba medio en bolas. Me salvó las papas, directo al grano como tiene que ser.',
    time: '09:15',
    verified: true
  }, {
    name: 'Pablo S.',
    msg: 'Por fin algo que no te da vueltas. La parte de la normativa AEA me sirvió una banda para empezar a pasar presupuestos como corresponde y que no me caminen. Recomendado.',
    time: '18:47',
    verified: true
  }, {
    name: 'Andrés C.',
    msg: 'Excelente el material. Soy técnico pero siempre se aprende algo nuevo, sobre todo con el tema de los cables y las protecciones. Un espectáculo.',
    time: '11:03',
    verified: true
  }];
  // Scroll reveal for sections
  useEffect(() => {
    const revealObserver = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('animate-reveal');
          revealObserver.unobserve(entry.target);
        }
      });
    }, {
      threshold: 0.1
    });
    document.querySelectorAll('[data-reveal]').forEach(el => {
      revealObserver.observe(el);
    });
    return () => revealObserver.disconnect();
  }, []);
  // Header visibility on scroll
  useEffect(() => {
    const handleScroll = () => {
      setHeaderVisible(window.scrollY > 100);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  useEffect(() => {
    const observer = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          if (entry.target.id === 'problem-real-section') setProblemRealVisible(true);
          if (entry.target.id === 'decision-section') setDecisionVisible(true);
          if (entry.target.id === 'benefits-section') setBenefitsVisible(true);
          if (entry.target.id === 'post-hero-section') setPostHeroVisible(true);
        }
      });
    }, {
      threshold: 0.15
    });
    const sections = ['problem-real-section', 'decision-section', 'benefits-section', 'post-hero-section'];
    sections.forEach(id => {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, []);
  const scrollToPurchase = () => {
    document.getElementById('compra')?.scrollIntoView({
      behavior: 'smooth'
    });
  };
  const handlePurchaseClick = () => {
    trackEvent('InitiateCheckout');
    window.location.href = 'https://checkout.maestroelectrico.shop/cart/47934593335518:1';
  };
  useEffect(() => {
    const observer = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          trackEvent('ViewContent', {
            content_name: 'Bonuses Section'
          });
          observer.disconnect();
        }
      });
    }, {
      threshold: 0.5
    });
    const bonusSection = document.getElementById('bonuses');
    if (bonusSection) observer.observe(bonusSection);
    return () => observer.disconnect();
  }, [trackEvent]);
  const showMoreTestimonials = () => {
    setVisibleTestimonials(prev => Math.min(prev + 5, allChatReviews.length));
  };
  const handleSubmitReview = (_review: {
    name: string;
    text: string;
    stars: number;
  }) => {
    // Handle review submission
  };
  return <div className="min-h-screen text-gray-300" style={{
    background: DARK,
    fontFamily: "'Roboto', sans-serif"
  }} data-id="element-67">
      {notification && <PurchaseNotification name={notification.name} city={notification.city} isVisible={isVisible} data-id="element-68" />}

      {/* ===== STICKY HEADER ===== */}
      <header className={`py-4 fixed top-0 left-0 right-0 z-40 transition-all duration-400 ${headerVisible ? 'animate-header-slide' : 'opacity-0 -translate-y-full pointer-events-none'}`} style={{
      background: '#121212',
      borderBottom: '1px solid rgba(255,255,255,0.05)'
    }} data-id="element-69">
        <div className="container mx-auto px-4 max-w-7xl flex justify-between items-center" data-id="element-70">
          <div className="flex items-center gap-3" data-id="element-71">
            <div className="w-12 h-12 rounded-full overflow-hidden flex-shrink-0" style={{
            border: '2px solid #FFFFFF',
            boxShadow: '0 0 0 2px #FFD700 inset',
            padding: '2px',
            background: '#000000'
          }} data-id="element-72">
              <img src="https://cdn.magicpatterns.com/uploads/egv5LsA5wFRxX13HaDezFV/Diseno_sin_titulo_(13).png" alt="Oficio Eléctrico Logo" className="w-full h-full object-cover rounded-full" data-id="element-73" />
            </div>
          </div>
          <button onClick={scrollToPurchase} className="text-black font-bold py-2.5 px-6 rounded-lg text-sm transition-transform hover:scale-105 uppercase tracking-wide" style={{
          background: '#D4AF37',
          fontFamily: "'Inter', sans-serif"
        }} data-id="element-74">
            VER OFERTA
          </button>
        </div>
      </header>

      {/* ===== HERO SECTION ===== */}
      <section className="relative flex flex-col items-center overflow-hidden" style={{
      background: '#121212'
    }} data-id="element-75">
        {/* Full-width header image - absolute top */}
        <img src="https://cdn.magicpatterns.com/uploads/5vp9E6UiTg4aaf8LQKX51V/1336864a-5be1-4273-ac48-5ecb4ba2fa13.png" alt="OFICIO ELÉCTRICO - El camino a Maestro" className="w-full object-cover" style={{
        maxHeight: '220px'
      }} data-id="element-76" />

        <div className="container mx-auto px-4 relative z-10 flex flex-col items-center pt-6 pb-8" data-id="element-77">
          {/* Super-header */}
          <p className="text-center text-[10px] font-light uppercase tracking-[0.3em] mb-4 animate-hero-text" style={{
          animationDelay: '0s',
          color: '#A1A1A1',
          fontFamily: "'Inter', sans-serif"
        }} data-id="element-78">
            CURSO DE INICIACIÓN TÉCNICA PROFESIONAL
          </p>

          {/* Main Title (H1) */}
          <h1 className="text-4xl md:text-6xl font-black mb-3 leading-[1.05] text-center uppercase" style={{
          fontFamily: "'Inter', sans-serif"
        }} data-id="element-79">
            <span className="block animate-hero-text text-white" style={{
            animationDelay: '0.1s'
          }} data-id="element-80">
              CONVIÉRTETE EN
            </span>
            <span className="block animate-hero-text mt-1" style={{
            animationDelay: '0.2s',
            color: '#FFD700'
          }} data-id="element-81">
              ELECTRICISTA PROFESIONAL
            </span>
          </h1>

          {/* Promise Text */}
          <p className="text-lg md:text-xl text-center mb-4 animate-hero-text" style={{
          animationDelay: '0.25s',
          color: '#D4AF37',
          fontFamily: "'Playfair Display', serif",
          fontStyle: 'italic'
        }} data-id="element-82">
            Multiplicá tus ingresos de forma segura y constante.
          </p>

          {/* Mockup Image - LARGE, DOMINANT, CENTRAL */}
          <div className="mb-3 flex justify-center relative animate-hero-zoom w-full" style={{
          animationDelay: '0.3s'
        }} data-id="element-83">
            {/* Glow behind mockup */}
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[300px] h-[300px] rounded-full blur-[100px]" style={{
            background: 'rgba(255,215,0,0.08)'
          }} data-id="element-84"></div>
            <img src="https://cdn.magicpatterns.com/uploads/8keUu3KwUERDbztNf4iCtt/Diseno_sin_titulo_(12).png" alt="OFICIO ELÉCTRICO - El camino a Maestro" className="w-full max-w-[380px] md:max-w-xl drop-shadow-2xl cursor-pointer relative z-10 hover:scale-[1.02] transition-transform duration-700" style={{
            maxHeight: '50vh',
            objectFit: 'contain'
          }} onClick={scrollToPurchase} data-id="element-85" />
          </div>

          {/* Authority + Social Proof - above counter/CTA */}
          <div className="flex flex-col items-center justify-center gap-2 mb-5 animate-hero-text text-center" style={{
          animationDelay: '0.4s'
        }} data-id="element-86">
            <div className="flex items-center gap-1.5 mb-1" data-id="element-87">
              <div className="flex gap-0.5" data-id="element-88">
                {[1, 2, 3, 4, 5].map(s => <Star key={s} className="w-4 h-4 fill-amber-400 text-amber-400" data-id="element-89" />)}
              </div>
              <span className="text-[12px] text-white font-bold" data-id="element-90">4.9/5</span>
            </div>
            <p className="text-[11px] md:text-[12px] uppercase tracking-wider max-w-sm md:max-w-lg leading-relaxed" style={{
            color: 'rgba(255,255,255,0.55)'
          }} data-id="element-91">
              DE LOS CREADORES DE{' '}
              <span className="text-white font-bold" data-id="element-92">MAESTRO ELÉCTRICO</span> Y{' '}
              <span className="text-white font-bold" data-id="element-93">PLC MASTER</span> (MILES DE
              PROFESIONALES YA SE CAPACITARON)
            </p>
          </div>

          {/* Dynamic Counter - refined, medium size, subtle */}
          <div className="mb-6 text-center animate-hero-text" style={{
          animationDelay: '0.5s'
        }} data-id="element-94">
            <span className="text-xl md:text-2xl font-extrabold" style={{
            color: NEON,
            fontFamily: "'Montserrat', sans-serif",
            textShadow: '0 0 12px rgba(255,255,0,0.3)'
          }} data-id="element-95">
              +{counterValue.toLocaleString('es-AR')}
            </span>
            <span className="text-sm text-gray-300 font-medium uppercase tracking-wider ml-2" data-id="element-96">
              PERSONAS YA ADENTRO
            </span>
          </div>

          {/* CTA Button */}
          <div className="text-center animate-hero-text w-full md:w-auto mb-4" style={{
          animationDelay: '0.6s'
        }} data-id="element-97">
            <button onClick={scrollToPurchase} className="btn-shine animate-cta-alive w-full md:w-auto text-black text-lg md:text-xl font-extrabold py-4 px-10 rounded-lg shadow-lg transition-all inline-flex items-center justify-center gap-2 uppercase" style={{
            background: NEON,
            fontFamily: "'Montserrat', sans-serif"
          }} data-id="element-98">
              QUIERO ACCESO COMPLETO AHORA →
            </button>
          </div>

          {/* Trust Footer */}
          <p className="text-center text-[11px] animate-hero-text" style={{
          animationDelay: '0.7s',
          color: 'rgba(255,255,255,0.4)'
        }} data-id="element-99">
            🔒 Pago 100% encriptado · Garantía sin riesgo
          </p>
        </div>
      </section>

      {/* ===== SECTION 2: ECOSISTEMA DEL PRODUCTO ===== */}
      <section id="post-hero-section" className="py-8 px-4" style={{
      background: DARK
    }} data-id="element-100">
        <div className="container mx-auto max-w-6xl" data-id="element-101">
          <div className="text-center mb-8" data-reveal data-id="element-102">
            <h2 className="text-2xl md:text-4xl font-extrabold text-white uppercase tracking-wide mb-2" style={{
            fontFamily: "'Montserrat', sans-serif"
          }} data-id="element-103">
              TODO LO QUE NECESITÁS PARA SALIR A TRABAJAR MAÑANA MISMO
            </h2>
            <div className="h-1 w-24 mx-auto rounded-full" style={{
            background: NEON
          }} data-id="element-104"></div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-105">
            {/* Item 1 */}
            <div className={`rounded-xl p-6 transition-all duration-300 hover:-translate-y-1 hover:shadow-lg group ${postHeroVisible ? 'animate-stagger-in' : 'opacity-0'}`} style={{
            background: CHARCOAL,
            border: '1px solid rgba(255,255,0,0.15)',
            animationDelay: '0ms',
            animationFillMode: 'forwards'
          }} data-id="element-106">
              <div className="w-12 h-12 rounded-lg flex items-center justify-center mb-4 transition-colors group-hover:bg-[rgba(255,255,0,0.1)]" style={{
              background: 'rgba(255,255,0,0.05)',
              border: '1px solid rgba(255,255,0,0.2)'
            }} data-id="element-107">
                <BookOpen className="w-6 h-6" style={{
                color: NEON
              }} data-id="element-108" />
              </div>
              <h3 className="text-lg font-bold text-white mb-2 uppercase" style={{
              fontFamily: "'Montserrat', sans-serif"
            }} data-id="element-109">
                MANUAL MAESTRO (VOL. 1 Y 2)
              </h3>
              <p className="text-gray-400 text-sm leading-relaxed" data-id="element-110">
                El núcleo teórico que te enseña desde magnitudes eléctricas
                básicas hasta la ejecución de instalaciones domiciliarias
                completas.
              </p>
            </div>

            {/* Item 2 */}
            <div className={`rounded-xl p-6 transition-all duration-300 hover:-translate-y-1 hover:shadow-lg group ${postHeroVisible ? 'animate-stagger-in' : 'opacity-0'}`} style={{
            background: CHARCOAL,
            border: '1px solid rgba(255,255,0,0.15)',
            animationDelay: '100ms',
            animationFillMode: 'forwards'
          }} data-id="element-111">
              <div className="w-12 h-12 rounded-lg flex items-center justify-center mb-4 transition-colors group-hover:bg-[rgba(255,255,0,0.1)]" style={{
              background: 'rgba(255,255,0,0.05)',
              border: '1px solid rgba(255,255,0,0.2)'
            }} data-id="element-112">
                <Video className="w-6 h-6" style={{
                color: NEON
              }} data-id="element-113" />
              </div>
              <h3 className="text-lg font-bold text-white mb-2 uppercase" style={{
              fontFamily: "'Montserrat', sans-serif"
            }} data-id="element-114">
                BIBLIOTECA DE VIDEOS PASO A PASO
              </h3>
              <p className="text-gray-400 text-sm leading-relaxed" data-id="element-115">
                Clases prácticas sobre conexión de tomacorrientes, llaves
                combinadas, instalación de ventiladores de techo y armado de
                prolongaciones.
              </p>
            </div>

            {/* Item 3 */}
            <div className={`rounded-xl p-6 transition-all duration-300 hover:-translate-y-1 hover:shadow-lg group ${postHeroVisible ? 'animate-stagger-in' : 'opacity-0'}`} style={{
            background: CHARCOAL,
            border: '1px solid rgba(255,255,0,0.15)',
            animationDelay: '200ms',
            animationFillMode: 'forwards'
          }} data-id="element-116">
              <div className="w-12 h-12 rounded-lg flex items-center justify-center mb-4 transition-colors group-hover:bg-[rgba(255,255,0,0.1)]" style={{
              background: 'rgba(255,255,0,0.05)',
              border: '1px solid rgba(255,255,0,0.2)'
            }} data-id="element-117">
                <Shield className="w-6 h-6" style={{
                color: NEON
              }} data-id="element-118" />
              </div>
              <h3 className="text-lg font-bold text-white mb-2 uppercase" style={{
              fontFamily: "'Montserrat', sans-serif"
            }} data-id="element-119">
                GUÍAS DE NORMATIVA AEA E IRAM
              </h3>
              <p className="text-gray-400 text-sm leading-relaxed" data-id="element-120">
                Acceso a la reglamentación oficial (AEA 90364) para que tus
                trabajos cumplan con los estándares de seguridad de Argentina.
              </p>
            </div>

            {/* Item 4 */}
            <div className={`rounded-xl p-6 transition-all duration-300 hover:-translate-y-1 hover:shadow-lg group ${postHeroVisible ? 'animate-stagger-in' : 'opacity-0'}`} style={{
            background: CHARCOAL,
            border: '1px solid rgba(255,255,0,0.15)',
            animationDelay: '300ms',
            animationFillMode: 'forwards'
          }} data-id="element-121">
              <div className="w-12 h-12 rounded-lg flex items-center justify-center mb-4 transition-colors group-hover:bg-[rgba(255,255,0,0.1)]" style={{
              background: 'rgba(255,255,0,0.05)',
              border: '1px solid rgba(255,255,0,0.2)'
            }} data-id="element-122">
                <CircuitBoard className="w-6 h-6" style={{
                color: NEON
              }} data-id="element-123" />
              </div>
              <h3 className="text-lg font-bold text-white mb-2 uppercase" style={{
              fontFamily: "'Montserrat', sans-serif"
            }} data-id="element-124">
                DIAGRAMAS Y TABLAS TÉCNICAS
              </h3>
              <p className="text-gray-400 text-sm leading-relaxed" data-id="element-125">
                Esquemas unifilares y multifilares de tableros, junto con tablas
                de sección de cables y corrientes admisibles.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ===== SECTION 3: LO QUE TE IMPIDE PROGRESAR ===== */}
      <section id="problem-real-section" className="py-8 px-4 animate-gradient-slow relative overflow-hidden" style={{
      background: `linear-gradient(135deg, #0a0a0a 0%, ${CHARCOAL} 50%, #0a0a0a 100%)`
    }} data-id="element-126">
        <div className="container mx-auto max-w-5xl relative z-10" data-id="element-127">
          <h3 className={`text-3xl md:text-4xl font-extrabold mb-8 text-center uppercase tracking-wide transition-all duration-700 ${problemRealVisible ? 'animate-stagger-in' : 'opacity-0'}`} style={{
          fontFamily: "'Montserrat', sans-serif",
          color: '#FF3333'
        }} data-id="element-128">
            LO QUE TE IMPIDE PROGRESAR
          </h3>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-129">
            {/* Problem 1 */}
            <div className={`backdrop-blur-sm rounded-xl p-8 shadow-lg transition-all duration-500 ${problemRealVisible ? 'animate-stagger-in' : 'opacity-0'}`} style={{
            background: CHARCOAL,
            border: '1px solid rgba(255,255,0,0.1)',
            animationDelay: '0.2s',
            animationFillMode: 'forwards'
          }} data-id="element-130">
              <div className="flex items-start gap-4" data-id="element-131">
                <div className="w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0" style={{
                background: 'rgba(255,51,51,0.05)',
                border: '1px solid rgba(255,51,51,0.2)'
              }} data-id="element-132">
                  <Zap className="w-6 h-6" style={{
                  color: '#FF3333'
                }} data-id="element-133" />
                </div>
                <div data-id="element-134">
                  <h4 className="font-bold text-white text-lg mb-2 uppercase" style={{
                  fontFamily: "'Montserrat', sans-serif"
                }} data-id="element-135">
                    EL MIEDO AL "CHISPAZO"
                  </h4>
                  <p className="text-gray-400 leading-relaxed" data-id="element-136">
                    Sentir que la electricidad es peligrosa o para genios,
                    cuando en realidad es un método de seguridad que cualquiera
                    puede aprender con el paso a paso correcto.
                  </p>
                </div>
              </div>
            </div>

            {/* Problem 2 */}
            <div className={`backdrop-blur-sm rounded-xl p-8 shadow-lg transition-all duration-500 ${problemRealVisible ? 'animate-stagger-in' : 'opacity-0'}`} style={{
            background: CHARCOAL,
            border: '1px solid rgba(255,255,0,0.1)',
            animationDelay: '0.3s',
            animationFillMode: 'forwards'
          }} data-id="element-137">
              <div className="flex items-start gap-4" data-id="element-138">
                <div className="w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0" style={{
                background: 'rgba(255,51,51,0.05)',
                border: '1px solid rgba(255,51,51,0.2)'
              }} data-id="element-139">
                  <DollarSign className="w-6 h-6" style={{
                  color: '#FF3333'
                }} data-id="element-140" />
                </div>
                <div data-id="element-141">
                  <h4 className="font-bold text-white text-lg mb-2 uppercase" style={{
                  fontFamily: "'Montserrat', sans-serif"
                }} data-id="element-142">
                    EL TECHO DE LAS "CHANGAS"
                  </h4>
                  <p className="text-gray-400 leading-relaxed" data-id="element-143">
                    Estar cansado de cobrar poco por trabajos básicos porque no
                    tenés el respaldo de saber el "porqué" técnico de cada
                    conexión.
                  </p>
                </div>
              </div>
            </div>

            {/* Problem 3 */}
            <div className={`backdrop-blur-sm rounded-xl p-8 shadow-lg transition-all duration-500 ${problemRealVisible ? 'animate-stagger-in' : 'opacity-0'}`} style={{
            background: CHARCOAL,
            border: '1px solid rgba(255,255,0,0.1)',
            animationDelay: '0.4s',
            animationFillMode: 'forwards'
          }} data-id="element-144">
              <div className="flex items-start gap-4" data-id="element-145">
                <div className="w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0" style={{
                background: 'rgba(255,51,51,0.05)',
                border: '1px solid rgba(255,51,51,0.2)'
              }} data-id="element-146">
                  <AlertTriangle className="w-6 h-6" style={{
                  color: '#FF3333'
                }} data-id="element-147" />
                </div>
                <div data-id="element-148">
                  <h4 className="font-bold text-white text-lg mb-2 uppercase" style={{
                  fontFamily: "'Montserrat', sans-serif"
                }} data-id="element-149">
                    LA INVISIBILIDAD ECONÓMICA
                  </h4>
                  <p className="text-gray-400 leading-relaxed" data-id="element-150">
                    Ver cómo otros crecen con su oficio mientras vos seguís
                    atrapado en trabajos mal pagados por no tener una
                    especialidad técnica.
                  </p>
                </div>
              </div>
            </div>

            {/* Problem 4 */}
            <div className={`backdrop-blur-sm rounded-xl p-8 shadow-lg transition-all duration-500 ${problemRealVisible ? 'animate-stagger-in' : 'opacity-0'}`} style={{
            background: CHARCOAL,
            border: '1px solid rgba(255,255,0,0.1)',
            animationDelay: '0.5s',
            animationFillMode: 'forwards'
          }} data-id="element-151">
              <div className="flex items-start gap-4" data-id="element-152">
                <div className="w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0" style={{
                background: 'rgba(255,51,51,0.05)',
                border: '1px solid rgba(255,51,51,0.2)'
              }} data-id="element-153">
                  <Brain className="w-6 h-6" style={{
                  color: '#FF3333'
                }} data-id="element-154" />
                </div>
                <div data-id="element-155">
                  <h4 className="font-bold text-white text-lg mb-2 uppercase" style={{
                  fontFamily: "'Montserrat', sans-serif"
                }} data-id="element-156">
                    EL LABERINTO DE YOUTUBE
                  </h4>
                  <p className="text-gray-400 leading-relaxed" data-id="element-157">
                    Perder horas en videos sueltos que te confunden y no te
                    enseñan a diagnosticar fallas reales ni a cobrar lo que vale
                    tu tiempo.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ===== SECTION 4: LA SOLUCIÓN DEFINITIVA ===== */}
      <section id="solution" className="py-8 px-4 text-white relative overflow-hidden" style={{
      background: `linear-gradient(180deg, ${CHARCOAL} 0%, ${DARK} 50%, ${CHARCOAL} 100%)`
    }} data-id="element-158">
        <div className="container mx-auto max-w-5xl relative z-10" data-id="element-159">
          <div className="text-center mb-8" data-id="element-160">
            <span className="font-bold px-4 py-1.5 rounded-full text-xs uppercase tracking-wider mb-2 inline-block text-black" style={{
            background: '#FFD700'
          }} data-id="element-161">
              LA SOLUCIÓN DEFINITIVA
            </span>
            <h2 className="text-3xl md:text-5xl font-extrabold mb-3 uppercase leading-tight" style={{
            fontFamily: "'Inter', sans-serif"
          }} data-id="element-162">
              DOMINÁ EL{' '}
              <span style={{
              color: '#FFD700'
            }} data-id="element-163">
                MANUAL OFICIO ELÉCTRICO
              </span>
            </h2>
            <p className="text-base md:text-lg max-w-3xl mx-auto" style={{
            color: '#A1A1A1',
            fontFamily: "'Inter', sans-serif"
          }} data-id="element-164">
              Esta es la hoja de ruta técnica diseñada para que pases de cero a
              realizar instalaciones profesionales con total seguridad y
              criterio técnico.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-10 items-center mb-12" data-id="element-165">
            <div className="space-y-6" data-id="element-166">
              {/* Solution 1 */}
              <div className="flex items-start gap-4 p-5 rounded-xl transition-colors" style={{
              background: 'rgba(26, 26, 26, 0.8)',
              border: '1px solid rgba(255, 255, 0, 0.15)'
            }} data-id="element-167">
                <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 mt-1" style={{
                background: 'rgba(255, 255, 0, 0.1)'
              }} data-id="element-168">
                  <Check className="w-5 h-5 font-bold" style={{
                  color: NEON
                }} data-id="element-169" />
                </div>
                <div data-id="element-170">
                  <h4 className="font-bold text-white text-lg mb-1" style={{
                  fontFamily: "'Montserrat', sans-serif"
                }} data-id="element-171">
                    APRENDIZAJE ESTRUCTURADO
                  </h4>
                  <p className="text-sm leading-relaxed" style={{
                  color: 'rgba(200,200,200,0.8)'
                }} data-id="element-172">
                    De la teoría atómica a la instalación de una ducha eléctrica
                    o un interruptor horario digital.
                  </p>
                </div>
              </div>

              {/* Solution 2 */}
              <div className="flex items-start gap-4 p-5 rounded-xl transition-colors" style={{
              background: 'rgba(26, 26, 26, 0.8)',
              border: '1px solid rgba(255, 255, 0, 0.15)'
            }} data-id="element-173">
                <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 mt-1" style={{
                background: 'rgba(255, 255, 0, 0.1)'
              }} data-id="element-174">
                  <Check className="w-5 h-5 font-bold" style={{
                  color: NEON
                }} data-id="element-175" />
                </div>
                <div data-id="element-176">
                  <h4 className="font-bold text-white text-lg mb-1" style={{
                  fontFamily: "'Montserrat', sans-serif"
                }} data-id="element-177">
                    FOCO EN LA SEGURIDAD
                  </h4>
                  <p className="text-sm leading-relaxed" style={{
                  color: 'rgba(200,200,200,0.8)'
                }} data-id="element-178">
                    Dominá el uso del interruptor diferencial y el sistema de
                    puesta a tierra para proteger vidas y equipos.
                  </p>
                </div>
              </div>

              {/* Solution 3 */}
              <div className="flex items-start gap-4 p-5 rounded-xl transition-colors" style={{
              background: 'rgba(26, 26, 26, 0.8)',
              border: '1px solid rgba(255, 255, 0, 0.15)'
            }} data-id="element-179">
                <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 mt-1" style={{
                background: 'rgba(255, 255, 0, 0.1)'
              }} data-id="element-180">
                  <Check className="w-5 h-5 font-bold" style={{
                  color: NEON
                }} data-id="element-181" />
                </div>
                <div data-id="element-182">
                  <h4 className="font-bold text-white text-lg mb-1" style={{
                  fontFamily: "'Montserrat', sans-serif"
                }} data-id="element-183">
                    CRITERIO TÉCNICO REAL
                  </h4>
                  <p className="text-sm leading-relaxed" style={{
                  color: 'rgba(200,200,200,0.8)'
                }} data-id="element-184">
                    Aprendé a leer simbología normalizada y a ejecutar planos
                    para que nadie cuestione tu trabajo.
                  </p>
                </div>
              </div>
            </div>

            <div className="flex justify-center" data-id="element-185">
              <div className="relative" data-id="element-186">
                <div className="absolute inset-0 blur-3xl rounded-full" style={{
                background: 'rgba(255, 255, 0, 0.1)'
              }} data-id="element-187"></div>
                <video src="https://cdn.magicpatterns.com/uploads/u8ASpkxKBGu1RSEHdGPYVm/0311(2).mp4" className="w-full max-w-sm drop-shadow-2xl rounded-lg relative z-10 transform hover:scale-105 transition-transform duration-500" autoPlay loop muted playsInline data-id="element-188" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ===== SECTION 5: BONUSES ===== */}
      <section id="bonuses" className="py-8 px-4" style={{
      background: '#121212'
    }} data-id="element-189">
        <div className="container mx-auto max-w-6xl" data-id="element-190">
          {/* Section Header */}
          <div className="text-center mb-8" data-reveal data-id="element-191">
            <h2 className="text-2xl md:text-4xl font-bold mb-3 uppercase text-white leading-tight" style={{
            fontFamily: "'Inter', sans-serif"
          }} data-id="element-192">
              ¡LLEVATE ESTOS{' '}
              <span className="px-2 rounded" style={{
              color: '#FFD700',
              background: 'rgba(255, 215, 0, 0.1)'
            }} data-id="element-193">
                6
              </span>{' '}
              BONOS DE LANZAMIENTO EXCLUSIVOS!
            </h2>
            <p className="text-sm md:text-base text-[#A1A1A1] max-w-2xl mx-auto mb-6" data-id="element-194">
              Material técnico, guías de instalación y tablas de cálculo
              diseñadas para electricistas que buscan resultados profesionales.
            </p>
            <div className="inline-flex items-center gap-2 px-5 py-2 rounded-full font-bold text-xs uppercase tracking-wide border" style={{
            background: 'rgba(255,215,0,0.08)',
            color: '#FFD700',
            borderColor: 'rgba(255,215,0,0.25)'
          }} data-id="element-195">
              <Clock className="w-3.5 h-3.5" data-id="element-196" />
              GRATIS POR TIEMPO LIMITADO
            </div>
          </div>

          {/* Horizontal Carousel */}
          <div className="relative max-w-5xl mx-auto" data-id="element-197">
            <div ref={bonusScrollRef} className="flex gap-6 overflow-x-auto pb-6 snap-x snap-mandatory hide-scrollbar" style={{
            scrollBehavior: 'smooth',
            WebkitOverflowScrolling: 'touch'
          }} onScroll={e => {
            const scrollLeft = e.currentTarget.scrollLeft;
            const maxScroll = e.currentTarget.scrollWidth - e.currentTarget.clientWidth;
            if (maxScroll > 0) {
              const progress = scrollLeft / maxScroll;
              const newIndex = Math.round(progress * 5);
              if (newIndex !== activeBonus) setActiveBonus(newIndex);
            }
          }} data-id="element-198">
              {/* Bonus 1 */}
              <div onClick={scrollToPurchase} className="cursor-pointer group flex-shrink-0 w-[280px] md:w-[320px] snap-center rounded-xl overflow-visible relative flex flex-col" style={{
              background: '#0D0D0D',
              border: '1px solid rgba(255,215,0,0.15)'
            }} data-id="element-199">
                <div className="relative pt-6 px-4 flex-grow flex items-center justify-center" data-id="element-200">
                  <div className="absolute top-3 right-3 z-10 flex flex-col items-end gap-1" data-id="element-201">
                    <span className="text-[9px] font-bold text-[#A1A1A1] uppercase tracking-wider bg-black/80 border border-white/10 backdrop-blur-sm px-2.5 py-1 rounded-full" data-id="element-202">
                      VALORADO EN $18.500 ARS
                    </span>
                    <span className="text-[11px] font-extrabold text-[#FFD700] uppercase tracking-wide bg-black/80 border border-[#FFD700]/30 backdrop-blur-sm px-2.5 py-0.5 rounded-full shadow-[0_0_10px_rgba(255,215,0,0.2)]" data-id="element-203">
                      ¡HOY GRATIS!
                    </span>
                  </div>
                  <img src="https://cdn.magicpatterns.com/uploads/qafXikvdsChuMEU766vKCE/Gemini_Generated_Image_xrntjixrntjixrnt.png" alt="Bono #1 - Normativa AEA Resumida" className="w-full h-auto object-contain max-h-[280px] drop-shadow-2xl group-hover:scale-105 transition-transform duration-500" data-id="element-204" />
                </div>
                <div className="p-5 border-t border-white/5 mt-4" data-id="element-205">
                  <h3 className="font-bold text-lg mb-2 uppercase leading-tight" style={{
                  color: '#FFD700',
                  fontFamily: "'Inter', sans-serif"
                }} data-id="element-206">
                    NORMATIVA AEA RESUMIDA
                  </h3>
                  <p className="text-sm text-white/80 leading-relaxed" data-id="element-207">
                    Reglamento oficial resumido para ejecutar instalaciones
                    eléctricas seguras.
                  </p>
                </div>
              </div>

              {/* Bonus 2 */}
              <div onClick={scrollToPurchase} className="cursor-pointer group flex-shrink-0 w-[280px] md:w-[320px] snap-center rounded-xl overflow-visible relative flex flex-col" style={{
              background: '#0D0D0D',
              border: '1px solid rgba(255,215,0,0.15)'
            }} data-id="element-208">
                <div className="relative pt-6 px-4 flex-grow flex items-center justify-center" data-id="element-209">
                  <div className="absolute top-3 right-3 z-10 flex flex-col items-end gap-1" data-id="element-210">
                    <span className="text-[9px] font-bold text-[#A1A1A1] uppercase tracking-wider bg-black/80 border border-white/10 backdrop-blur-sm px-2.5 py-1 rounded-full" data-id="element-211">
                      VALORADO EN $18.500 ARS
                    </span>
                    <span className="text-[11px] font-extrabold text-[#FFD700] uppercase tracking-wide bg-black/80 border border-[#FFD700]/30 backdrop-blur-sm px-2.5 py-0.5 rounded-full shadow-[0_0_10px_rgba(255,215,0,0.2)]" data-id="element-212">
                      ¡HOY GRATIS!
                    </span>
                  </div>
                  <img src="https://cdn.magicpatterns.com/uploads/uFUnxh4MtsGrw7BPADbqVg/Gemini_Generated_Image_wwa4cswwa4cswwa4.png" alt="Bono #2 - Guía de Instalaciones" className="w-full h-auto object-contain max-h-[280px] drop-shadow-2xl group-hover:scale-105 transition-transform duration-500" data-id="element-213" />
                </div>
                <div className="p-5 border-t border-white/5 mt-4" data-id="element-214">
                  <h3 className="font-bold text-lg mb-2 uppercase leading-tight" style={{
                  color: '#FFD700',
                  fontFamily: "'Inter', sans-serif"
                }} data-id="element-215">
                    GUÍA DE INSTALACIONES
                  </h3>
                  <p className="text-sm text-white/80 leading-relaxed" data-id="element-216">
                    El paso a paso profesional para cableados, tableros y
                    protecciones.
                  </p>
                </div>
              </div>

              {/* Bonus 3 */}
              <div onClick={scrollToPurchase} className="cursor-pointer group flex-shrink-0 w-[280px] md:w-[320px] snap-center rounded-xl overflow-visible relative flex flex-col" style={{
              background: '#0D0D0D',
              border: '1px solid rgba(255,215,0,0.15)'
            }} data-id="element-217">
                <div className="relative pt-6 px-4 flex-grow flex items-center justify-center" data-id="element-218">
                  <div className="absolute top-3 right-3 z-10 flex flex-col items-end gap-1" data-id="element-219">
                    <span className="text-[9px] font-bold text-[#A1A1A1] uppercase tracking-wider bg-black/80 border border-white/10 backdrop-blur-sm px-2.5 py-1 rounded-full" data-id="element-220">
                      VALORADO EN $18.500 ARS
                    </span>
                    <span className="text-[11px] font-extrabold text-[#FFD700] uppercase tracking-wide bg-black/80 border border-[#FFD700]/30 backdrop-blur-sm px-2.5 py-0.5 rounded-full shadow-[0_0_10px_rgba(255,215,0,0.2)]" data-id="element-221">
                      ¡HOY GRATIS!
                    </span>
                  </div>
                  <img src="https://cdn.magicpatterns.com/uploads/gqc8ptfgWztenvfoXKSR1w/Gemini_Generated_Image_nocz85nocz85nocz.png" alt="Bono #3 - Simbología Normalizada" className="w-full h-auto object-contain max-h-[280px] drop-shadow-2xl group-hover:scale-105 transition-transform duration-500" data-id="element-222" />
                </div>
                <div className="p-5 border-t border-white/5 mt-4" data-id="element-223">
                  <h3 className="font-bold text-lg mb-2 uppercase leading-tight" style={{
                  color: '#FFD700',
                  fontFamily: "'Inter', sans-serif"
                }} data-id="element-224">
                    SIMBOLOGÍA NORMALIZADA
                  </h3>
                  <p className="text-sm text-white/80 leading-relaxed" data-id="element-225">
                    Guía rápida de símbolos eléctricos para interpretar
                    cualquier plano en obra.
                  </p>
                </div>
              </div>

              {/* Bonus 4 */}
              <div onClick={scrollToPurchase} className="cursor-pointer group flex-shrink-0 w-[280px] md:w-[320px] snap-center rounded-xl overflow-visible relative flex flex-col" style={{
              background: '#0D0D0D',
              border: '1px solid rgba(255,215,0,0.15)'
            }} data-id="element-226">
                <div className="relative pt-6 px-4 flex-grow flex items-center justify-center" data-id="element-227">
                  <div className="absolute top-3 right-3 z-10 flex flex-col items-end gap-1" data-id="element-228">
                    <span className="text-[9px] font-bold text-[#A1A1A1] uppercase tracking-wider bg-black/80 border border-white/10 backdrop-blur-sm px-2.5 py-1 rounded-full" data-id="element-229">
                      VALORADO EN $18.500 ARS
                    </span>
                    <span className="text-[11px] font-extrabold text-[#FFD700] uppercase tracking-wide bg-black/80 border border-[#FFD700]/30 backdrop-blur-sm px-2.5 py-0.5 rounded-full shadow-[0_0_10px_rgba(255,215,0,0.2)]" data-id="element-230">
                      ¡HOY GRATIS!
                    </span>
                  </div>
                  <img src="https://cdn.magicpatterns.com/uploads/wL8eaPrCQSrTAoEko3tJV3/Gemini_Generated_Image_viawacviawacviaw.png" alt="Bono #4 - Cálculo de Conductores" className="w-full h-auto object-contain max-h-[280px] drop-shadow-2xl group-hover:scale-105 transition-transform duration-500" data-id="element-231" />
                </div>
                <div className="p-5 border-t border-white/5 mt-4" data-id="element-232">
                  <h3 className="font-bold text-lg mb-2 uppercase leading-tight" style={{
                  color: '#FFD700',
                  fontFamily: "'Inter', sans-serif"
                }} data-id="element-233">
                    CÁLCULO DE CONDUCTORES
                  </h3>
                  <p className="text-sm text-white/80 leading-relaxed" data-id="element-234">
                    Tablas de corriente y cálculo de caída de tensión según
                    norma AEA.
                  </p>
                </div>
              </div>

              {/* Bonus 5 */}
              <div onClick={scrollToPurchase} className="cursor-pointer group flex-shrink-0 w-[280px] md:w-[320px] snap-center rounded-xl overflow-visible relative flex flex-col" style={{
              background: '#0D0D0D',
              border: '1px solid rgba(255,215,0,0.15)'
            }} data-id="element-235">
                <div className="relative pt-6 px-4 flex-grow flex items-center justify-center" data-id="element-236">
                  <div className="absolute top-3 right-3 z-10 flex flex-col items-end gap-1" data-id="element-237">
                    <span className="text-[9px] font-bold text-[#A1A1A1] uppercase tracking-wider bg-black/80 border border-white/10 backdrop-blur-sm px-2.5 py-1 rounded-full" data-id="element-238">
                      VALORADO EN $18.500 ARS
                    </span>
                    <span className="text-[11px] font-extrabold text-[#FFD700] uppercase tracking-wide bg-black/80 border border-[#FFD700]/30 backdrop-blur-sm px-2.5 py-0.5 rounded-full shadow-[0_0_10px_rgba(255,215,0,0.2)]" data-id="element-239">
                      ¡HOY GRATIS!
                    </span>
                  </div>
                  <img src="https://cdn.magicpatterns.com/uploads/h7j3cWp99nTU9iL7rD6Ffw/Gemini_Generated_Image_gq324lgq324lgq32.png" alt="Bono #5 - Instalación de Medidores" className="w-full h-auto object-contain max-h-[280px] drop-shadow-2xl group-hover:scale-105 transition-transform duration-500" data-id="element-240" />
                </div>
                <div className="p-5 border-t border-white/5 mt-4" data-id="element-241">
                  <h3 className="font-bold text-lg mb-2 uppercase leading-tight" style={{
                  color: '#FFD700',
                  fontFamily: "'Inter', sans-serif"
                }} data-id="element-242">
                    INSTALACIÓN DE MEDIDORES
                  </h3>
                  <p className="text-sm text-white/80 leading-relaxed" data-id="element-243">
                    Todo sobre acometidas y pilares monofásicos/trifásicos.
                  </p>
                </div>
              </div>

              {/* Bonus 6 */}
              <div onClick={scrollToPurchase} className="cursor-pointer group flex-shrink-0 w-[280px] md:w-[320px] snap-center rounded-xl overflow-visible relative flex flex-col" style={{
              background: '#0D0D0D',
              border: '1px solid rgba(255,215,0,0.15)'
            }} data-id="element-244">
                <div className="relative pt-6 px-4 flex-grow flex items-center justify-center" data-id="element-245">
                  <div className="absolute top-3 right-3 z-10 flex flex-col items-end gap-1" data-id="element-246">
                    <span className="text-[9px] font-bold text-[#A1A1A1] uppercase tracking-wider bg-black/80 border border-white/10 backdrop-blur-sm px-2.5 py-1 rounded-full" data-id="element-247">
                      VALORADO EN $18.500 ARS
                    </span>
                    <span className="text-[11px] font-extrabold text-[#FFD700] uppercase tracking-wide bg-black/80 border border-[#FFD700]/30 backdrop-blur-sm px-2.5 py-0.5 rounded-full shadow-[0_0_10px_rgba(255,215,0,0.2)]" data-id="element-248">
                      ¡HOY GRATIS!
                    </span>
                  </div>
                  <img src="https://cdn.magicpatterns.com/uploads/eN6a86ofD3gs9Rv2TDnJo6/Gemini_Generated_Image_lon1l0lon1l0lon1.png" alt="Bono #6 - Ventiladores de Techo" className="w-full h-auto object-contain max-h-[280px] drop-shadow-2xl group-hover:scale-105 transition-transform duration-500" data-id="element-249" />
                </div>
                <div className="p-5 border-t border-white/5 mt-4" data-id="element-250">
                  <h3 className="font-bold text-lg mb-2 uppercase leading-tight" style={{
                  color: '#FFD700',
                  fontFamily: "'Inter', sans-serif"
                }} data-id="element-251">
                    VENTILADORES DE TECHO
                  </h3>
                  <p className="text-sm text-white/80 leading-relaxed" data-id="element-252">
                    Diagramas y paso a paso para armado, balanceo e instalación
                    segura.
                  </p>
                </div>
              </div>
            </div>

            {/* Slider Progress Bar */}
            <div className="mt-4 flex flex-col items-center gap-3" data-id="element-253">
              <div className="w-full max-w-[200px] h-1 bg-gray-800 rounded-full overflow-hidden" data-id="element-254">
                <div className="h-full rounded-full transition-all duration-300 ease-out" style={{
                width: `${(activeBonus + 1) / 6 * 100}%`,
                background: '#FFD700'
              }} data-id="element-255"></div>
              </div>
            </div>
          </div>

          {/* Total Value Bar */}
          <div className="mt-10 text-center" data-id="element-256">
            <p className="text-sm md:text-base font-bold text-[#A1A1A1] uppercase tracking-widest" data-id="element-257">
              TOTAL VALOR ACUMULADO SÓLO EN BONOS:{' '}
              <span className="text-white" data-id="element-258">$111.000 ARS.</span>
            </p>
          </div>
        </div>
      </section>

      {/* ===== LO QUE LOGRÁS ===== */}
      <section id="benefits-section" className="py-8 px-4" style={{
      background: `linear-gradient(180deg, ${DARK} 0%, ${CHARCOAL} 100%)`
    }} data-id="element-259">
        <div className="container mx-auto max-w-5xl" data-id="element-260">
          <h2 className="text-3xl md:text-4xl font-bold mb-8 text-center uppercase text-white leading-tight" style={{
          fontFamily: "'Montserrat', sans-serif"
        }} data-id="element-261">
            LO QUE LOGRÁS CON{' '}
            <span style={{
            color: NEON
          }} data-id="element-262">
              OFICIO ELÉCTRICO
            </span>
          </h2>

          <div className="grid md:grid-cols-3 gap-8" data-id="element-263">
            {/* Benefit 1 */}
            <div className={`rounded-xl p-8 shadow-md transition-all duration-300 ${benefitsVisible ? 'animate-stagger-in' : 'opacity-0'}`} style={{
            background: CHARCOAL,
            border: '1px solid rgba(255,255,0,0.1)',
            animationDelay: '0ms',
            animationFillMode: 'forwards'
          }} data-id="element-264">
              <div className="flex items-center gap-3 mb-6" data-id="element-265">
                <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{
                background: 'rgba(255, 255, 0, 0.05)'
              }} data-id="element-266">
                  <Shield className="w-6 h-6" style={{
                  color: NEON
                }} data-id="element-267" />
                </div>
                <h3 className="font-bold text-white text-lg leading-tight" style={{
                fontFamily: "'Montserrat', sans-serif"
              }} data-id="element-268">
                  SEGURIDAD REAL
                </h3>
              </div>
              <div className="space-y-4 mb-6" data-id="element-269">
                <div className="flex items-start gap-3" data-id="element-270">
                  <Check className="w-5 h-5 mt-0.5 flex-shrink-0" style={{
                  color: NEON
                }} data-id="element-271" />
                  <span className="text-gray-400 text-sm leading-relaxed" data-id="element-272">
                    Dominá el interruptor diferencial y la puesta a tierra
                  </span>
                </div>
                <div className="flex items-start gap-3" data-id="element-273">
                  <Check className="w-5 h-5 mt-0.5 flex-shrink-0" style={{
                  color: NEON
                }} data-id="element-274" />
                  <span className="text-gray-400 text-sm leading-relaxed" data-id="element-275">
                    Trabajá con confianza sabiendo que cumplís la norma AEA
                  </span>
                </div>
              </div>
              <span className="inline-block text-xs font-bold px-3 py-1.5 rounded-full uppercase tracking-wide" style={{
              background: 'rgba(255, 255, 0, 0.1)',
              color: NEON
            }} data-id="element-276">
                PROTECCIÓN TOTAL
              </span>
            </div>

            {/* Benefit 2 */}
            <div className={`rounded-xl p-8 shadow-md transition-all duration-300 ${benefitsVisible ? 'animate-stagger-in' : 'opacity-0'}`} style={{
            background: CHARCOAL,
            border: '1px solid rgba(255,255,0,0.1)',
            animationDelay: '100ms',
            animationFillMode: 'forwards'
          }} data-id="element-277">
              <div className="flex items-center gap-3 mb-6" data-id="element-278">
                <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{
                background: 'rgba(255, 255, 0, 0.05)'
              }} data-id="element-279">
                  <Target className="w-6 h-6" style={{
                  color: NEON
                }} data-id="element-280" />
                </div>
                <h3 className="font-bold text-white text-lg leading-tight" style={{
                fontFamily: "'Montserrat', sans-serif"
              }} data-id="element-281">
                  CRITERIO PROFESIONAL
                </h3>
              </div>
              <div className="space-y-4 mb-6" data-id="element-282">
                <div className="flex items-start gap-3" data-id="element-283">
                  <Check className="w-5 h-5 mt-0.5 flex-shrink-0" style={{
                  color: NEON
                }} data-id="element-284" />
                  <span className="text-gray-400 text-sm leading-relaxed" data-id="element-285">
                    Leé planos y simbología normalizada sin dudar
                  </span>
                </div>
                <div className="flex items-start gap-3" data-id="element-286">
                  <Check className="w-5 h-5 mt-0.5 flex-shrink-0" style={{
                  color: NEON
                }} data-id="element-287" />
                  <span className="text-gray-400 text-sm leading-relaxed" data-id="element-288">
                    Presupuestá con fundamento técnico y cobrá lo justo
                  </span>
                </div>
              </div>
              <span className="inline-block text-xs font-bold px-3 py-1.5 rounded-full uppercase tracking-wide" style={{
              background: 'rgba(255, 255, 0, 0.1)',
              color: NEON
            }} data-id="element-289">
                CERO IMPROVISACIÓN
              </span>
            </div>

            {/* Benefit 3 */}
            <div className={`rounded-xl p-8 shadow-md transition-all duration-300 ${benefitsVisible ? 'animate-stagger-in' : 'opacity-0'}`} style={{
            background: CHARCOAL,
            border: '1px solid rgba(255,255,0,0.1)',
            animationDelay: '200ms',
            animationFillMode: 'forwards'
          }} data-id="element-290">
              <div className="flex items-center gap-3 mb-6" data-id="element-291">
                <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{
                background: 'rgba(255, 255, 0, 0.05)'
              }} data-id="element-292">
                  <TrendingUp className="w-6 h-6" style={{
                  color: NEON
                }} data-id="element-293" />
                </div>
                <h3 className="font-bold text-white text-lg leading-tight" style={{
                fontFamily: "'Montserrat', sans-serif"
              }} data-id="element-294">
                  SALTO ECONÓMICO
                </h3>
              </div>
              <div className="space-y-4 mb-6" data-id="element-295">
                <div className="flex items-start gap-3" data-id="element-296">
                  <Check className="w-5 h-5 mt-0.5 flex-shrink-0" style={{
                  color: NEON
                }} data-id="element-297" />
                  <span className="text-gray-400 text-sm leading-relaxed" data-id="element-298">
                    Pasá de changas a instalaciones profesionales bien pagas
                  </span>
                </div>
                <div className="flex items-start gap-3" data-id="element-299">
                  <Check className="w-5 h-5 mt-0.5 flex-shrink-0" style={{
                  color: NEON
                }} data-id="element-300" />
                  <span className="text-gray-400 text-sm leading-relaxed" data-id="element-301">
                    Convertite en el electricista de referencia de tu zona
                  </span>
                </div>
              </div>
              <span className="inline-block text-xs font-bold px-3 py-1.5 rounded-full uppercase tracking-wide" style={{
              background: 'rgba(255, 255, 0, 0.1)',
              color: NEON
            }} data-id="element-302">
                VALOR DE MERCADO
              </span>
            </div>
          </div>

          {/* Quote Box */}
          <div className="mt-16 p-8 md:p-10 rounded-2xl text-center shadow-xl max-w-4xl mx-auto transform hover:scale-[1.01] transition-transform" style={{
          background: NEON,
          color: 'black',
          boxShadow: '0 20px 40px rgba(255, 255, 0, 0.15)'
        }} data-id="element-303">
            <p className="text-xl md:text-2xl font-bold leading-relaxed" style={{
            fontFamily: "'Montserrat', sans-serif"
          }} data-id="element-304">
              "Dejá de ser invisible en el rubro. Invertir en tu formación
              eléctrica es asegurarte los mejores trabajos y el respeto de tus
              clientes."
            </p>
          </div>
        </div>
      </section>

      {/* ===== PACK SECTION ===== */}
      <section className="py-12 px-4" style={{
      background: `linear-gradient(180deg, ${CHARCOAL} 0%, ${DARK} 100%)`
    }} data-id="element-305">
        <div className="container mx-auto max-w-6xl" data-id="element-306">
          <div className="text-center mb-12" data-id="element-307">
            <h2 className="text-3xl md:text-4xl font-bold mb-4 text-white uppercase leading-tight" style={{
            fontFamily: "'Inter', sans-serif"
          }} data-id="element-308">
              LO QUE VAS A DOMINAR EN EL CURSO COMPLETO
            </h2>
            <p className="text-base text-[#A1A1A1] max-w-2xl mx-auto" data-id="element-309">
              Un programa integral diseñado para electricistas que buscan
              resultados profesionales y seguros.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-10 items-center" data-id="element-310">
            {/* Left Column: Modules List */}
            <div className="space-y-4" data-id="element-311">
              {/* Main Manual Highlighted */}
              <div className="flex items-start gap-4 p-4 rounded-lg border" style={{
              background: 'rgba(255,215,0,0.05)',
              borderColor: 'rgba(255,215,0,0.3)'
            }} data-id="element-312">
                <div className="mt-1 flex-shrink-0 bg-[#FFD700] rounded-full p-1" data-id="element-313">
                  <Check className="w-5 h-5 text-black font-bold" data-id="element-314" />
                </div>
                <div data-id="element-315">
                  <p className="text-lg text-[#FFD700] font-bold mb-1" data-id="element-316">
                    MANUAL OFICIO ELÉCTRICO
                  </p>
                  <p className="text-sm text-white/90 leading-relaxed" data-id="element-317">
                    Dominá la teoría y la práctica profesional para ejecutar
                    instalaciones seguras desde el primer día.
                  </p>
                </div>
              </div>

              {/* Bonus 1 */}
              <div className="flex items-start gap-4 p-2" data-id="element-318">
                <div className="mt-1 flex-shrink-0" data-id="element-319">
                  <Check className="w-5 h-5 text-[#FFD700]" data-id="element-320" />
                </div>
                <p className="text-base text-white font-medium" data-id="element-321">
                  <span className="font-bold text-[#FFD700]" data-id="element-322">
                    BONO 1: Normativa AEA Resumida:
                  </span>{' '}
                  Reglamento oficial para instalaciones seguras y profesionales.
                </p>
              </div>

              {/* Bonus 2 */}
              <div className="flex items-start gap-4 p-2" data-id="element-323">
                <div className="mt-1 flex-shrink-0" data-id="element-324">
                  <Check className="w-5 h-5 text-[#FFD700]" data-id="element-325" />
                </div>
                <p className="text-base text-white font-medium" data-id="element-326">
                  <span className="font-bold text-[#FFD700]" data-id="element-327">
                    BONO 2: Guía de Instalaciones:
                  </span>{' '}
                  El paso a paso profesional para cableados y tableros.
                </p>
              </div>

              {/* Bonus 3 */}
              <div className="flex items-start gap-4 p-2" data-id="element-328">
                <div className="mt-1 flex-shrink-0" data-id="element-329">
                  <Check className="w-5 h-5 text-[#FFD700]" data-id="element-330" />
                </div>
                <p className="text-base text-white font-medium" data-id="element-331">
                  <span className="font-bold text-[#FFD700]" data-id="element-332">
                    BONO 3: Simbología Normalizada:
                  </span>{' '}
                  Aprendé a leer e interpretar cualquier plano profesional.
                </p>
              </div>

              {/* Bonus 4 */}
              <div className="flex items-start gap-4 p-2" data-id="element-333">
                <div className="mt-1 flex-shrink-0" data-id="element-334">
                  <Check className="w-5 h-5 text-[#FFD700]" data-id="element-335" />
                </div>
                <p className="text-base text-white font-medium" data-id="element-336">
                  <span className="font-bold text-[#FFD700]" data-id="element-337">
                    BONO 4: Cálculo de Conductores:
                  </span>{' '}
                  Tablas de corriente y cálculo de caída de tensión.
                </p>
              </div>

              {/* Bonus 5 */}
              <div className="flex items-start gap-4 p-2" data-id="element-338">
                <div className="mt-1 flex-shrink-0" data-id="element-339">
                  <Check className="w-5 h-5 text-[#FFD700]" data-id="element-340" />
                </div>
                <p className="text-base text-white font-medium" data-id="element-341">
                  <span className="font-bold text-[#FFD700]" data-id="element-342">
                    BONO 5: Instalación de Medidores:
                  </span>{' '}
                  Todo sobre acometidas y pilares monofásicos/trifásicos.
                </p>
              </div>

              {/* Bonus 6 */}
              <div className="flex items-start gap-4 p-2" data-id="element-343">
                <div className="mt-1 flex-shrink-0" data-id="element-344">
                  <Check className="w-5 h-5 text-[#FFD700]" data-id="element-345" />
                </div>
                <p className="text-base text-white font-medium" data-id="element-346">
                  <span className="font-bold text-[#FFD700]" data-id="element-347">
                    BONO 6: Ventiladores de Techo:
                  </span>{' '}
                  Armado, balanceo e instalación segura de motores y variadores.
                </p>
              </div>
            </div>

            {/* Right Column: Pack Image */}
            <div className="relative flex justify-center" data-id="element-348">
              {/* Soft glow behind image */}
              <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[80%] h-[80%] rounded-full blur-[80px]" style={{
              background: 'rgba(255,215,0,0.05)'
            }} data-id="element-349"></div>
              <img src="https://cdn.magicpatterns.com/uploads/mPHyy16HRSakwTQLt1j749/Gemini_Generated_Image_tx7yz5tx7yz5tx7y.png" alt="Pack Completo Oficio Eléctrico" className="w-full max-w-lg object-contain relative z-10 drop-shadow-2xl" style={{
              filter: 'drop-shadow(0 20px 30px rgba(0,0,0,0.5))'
            }} data-id="element-350" />
            </div>
          </div>
        </div>
      </section>

      {/* ===== PURCHASE SECTION ===== */}
      <section id="compra" className="py-8 px-4" style={{
      background: `linear-gradient(180deg, ${DARK} 0%, ${CHARCOAL} 100%)`
    }} data-id="element-351">
        <div className="container mx-auto max-w-lg" data-id="element-352">
          <div className="rounded-2xl relative mb-6 overflow-hidden animate-card-glow" style={{
          background: CHARCOAL,
          border: `3px solid #FFD700`
        }} data-reveal data-id="element-353">
            {/* Top banner */}
            <div className="text-black font-extrabold py-3 px-4 text-center text-sm uppercase tracking-wider" style={{
            background: '#FFD700'
          }} data-id="element-354">
              DESCUENTO ESPECIAL DE LANZAMIENTO
            </div>

            <div className="p-6 md:p-8" data-id="element-355">
              {/* Offer Title */}
              <div className="text-center mb-2" data-id="element-356">
                <p className="text-sm font-bold text-white uppercase tracking-wider" data-id="element-357">
                  OFERTA EXCLUSIVA HOY:
                </p>
              </div>

              {/* Strikethrough price */}
              <div className="text-center mb-1" data-id="element-358">
                <p className="text-lg text-[#A1A1A1] font-bold" data-id="element-359">
                  <span className="line-through" data-id="element-360">$59.900 ARS</span>
                </p>
              </div>

              {/* Main price */}
              <div className="text-center mb-4" data-id="element-361">
                <p className="text-5xl md:text-6xl font-extrabold leading-tight uppercase" style={{
                color: '#FFD700',
                fontFamily: "'Inter', sans-serif"
              }} data-id="element-362">
                  $19.990 <span className="text-2xl" data-id="element-363">ARS</span>
                </p>
              </div>

              {/* Offer Text */}
              <div className="text-center mb-6" data-id="element-364">
                <p className="text-sm md:text-base text-white font-medium" data-id="element-365">
                  LLEVATE EL CURSO Y LOS 6 BONOS POR SÓLO $19.990 ARS.
                </p>
              </div>

              {/* Installments badge */}
              <div className="flex justify-center mb-6" data-id="element-366">
                <div className="font-semibold px-5 py-2 rounded-lg text-sm flex items-center gap-2 border" style={{
                background: 'rgba(255,215,0,0.05)',
                color: '#FFD700',
                borderColor: 'rgba(255,215,0,0.2)'
              }} data-id="element-367">
                  <CreditCard className="w-4 h-4" data-id="element-368" />
                  Hasta 3 cuotas sin interés
                </div>
              </div>

              {/* Urgency box */}
              <div className="rounded-lg px-5 py-4 mb-8" style={{
              background: DARK
            }} data-id="element-369">
                <div className="flex items-center gap-2 mb-2" data-id="element-370">
                  <span className="relative flex h-2.5 w-2.5 flex-shrink-0" data-id="element-371">
                    <span className="absolute inline-flex h-full w-full rounded-full bg-green-400 animate-green-blink" data-id="element-372"></span>
                  </span>
                  <span className="text-sm text-white font-bold" data-id="element-373">
                    {viewers} personas viendo esta página ahora
                  </span>
                </div>
                <div className="flex items-center gap-2" data-id="element-374">
                  <span className="relative flex h-2.5 w-2.5 flex-shrink-0" data-id="element-375">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75" data-id="element-376"></span>
                    <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-red-500" data-id="element-377"></span>
                  </span>
                  <span className="text-sm text-white font-bold" data-id="element-378">
                    Quedan {spots} cupos con precio promocional
                  </span>
                </div>
              </div>

              {/* Counter Widget - only counter, no stars/creadores */}
              <div className="rounded-lg px-4 py-3 mb-6 text-center border border-white/5" style={{
              background: DARK
            }} data-id="element-379">
                <div className="text-2xl font-extrabold mb-1 animate-neon-glow" style={{
                color: NEON,
                fontFamily: "'Montserrat', sans-serif"
              }} data-id="element-380">
                  {counterValue.toLocaleString('es-AR')}
                </div>
                <p className="text-xs text-gray-400 uppercase tracking-wider mb-1" data-id="element-381">
                  ALUMNOS YA ADENTRO
                </p>
              </div>

              {/* Main CTA Button */}
              <button onClick={handlePurchaseClick} className="btn-shine animate-cta-alive w-full text-black text-lg md:text-xl font-extrabold py-5 rounded-lg shadow-lg hover:shadow-xl transition-all flex items-center justify-center gap-2 mb-6 uppercase" style={{
              background: '#FFD700',
              fontFamily: "'Montserrat', sans-serif"
            }} data-id="element-382">
                SÍ, QUIERO ACCEDER POR $19.990
              </button>

              {/* Payment methods */}
              <div className="text-center mb-6" data-id="element-383">
                <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-3" data-id="element-384">
                  MEDIOS DE PAGO
                </p>
                <div className="flex justify-center" data-id="element-385">
                  <img src="https://cdn.magicpatterns.com/uploads/gxJ13jdAqycrJ4WCnDAsQm/mercadopago2.jpg" alt="Mercado Pago y tarjetas" className="w-full max-w-[320px] object-contain opacity-80" data-id="element-386" />
                </div>
              </div>

              {/* Trust footer text */}
              <div className="flex items-center justify-center gap-6 pt-4 border-t border-gray-800" data-id="element-387">
                <div className="flex items-center gap-1.5" data-id="element-388">
                  <Shield className="w-4 h-4 text-green-500" data-id="element-389" />
                  <span className="text-[10px] text-gray-500 font-semibold uppercase tracking-wide" data-id="element-390">
                    Pago 100% encriptado
                  </span>
                </div>
                <div className="w-px h-5 bg-gray-800" data-id="element-391"></div>
                <div className="flex items-center gap-1.5" data-id="element-392">
                  <img src="https://cdn.magicpatterns.com/uploads/tUHvrtYMuSGXVHTScHjzak/Selo-de-Garantia-de-7-Dias-PNG-Transparente-Sem-Fundo-1.png" alt="Garantía" className="w-5 h-5 object-contain opacity-70" data-id="element-393" />
                  <span className="text-[10px] text-gray-500 font-semibold uppercase tracking-wide" data-id="element-394">
                    Garantía sin riesgo
                  </span>
                </div>
              </div>
            </div>

            {/* Trust badges on dark background */}
            <div className="p-6 border-t border-gray-800" style={{
            background: `linear-gradient(135deg, ${DARK}, #0a0a0a)`
          }} data-id="element-395">
              <div className="grid grid-cols-2 gap-4 items-center" data-id="element-396">
                <div className="flex flex-col items-center gap-2" data-id="element-397">
                  <img src="https://cdn.magicpatterns.com/uploads/tUHvrtYMuSGXVHTScHjzak/Selo-de-Garantia-de-7-Dias-PNG-Transparente-Sem-Fundo-1.png" alt="Garantía de 7 días" className="w-full max-w-[120px] object-contain opacity-80" data-id="element-398" />
                  <span className="text-gray-400 text-xs font-medium uppercase tracking-wide text-center" data-id="element-399">
                    GARANTÍA DE SATISFACCIÓN
                  </span>
                </div>
                <div className="flex flex-col items-center gap-2" data-id="element-400">
                  <img src="https://cdn.magicpatterns.com/uploads/bmodW1kGNGDhBh2dosY7h5/safe-purchase-7779878_1280.png" alt="Compra Segura" className="w-full max-w-[156px] object-contain opacity-80" data-id="element-401" />
                  <span className="text-gray-400 text-xs font-medium uppercase tracking-wide text-center" data-id="element-402">
                    COMPRA 100% SEGURA
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Post-Purchase Benefits */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-center py-4" data-id="element-403">
            <div className="flex flex-col items-center gap-2 p-3 rounded-lg border" style={{
            background: CHARCOAL,
            borderColor: 'rgba(255,255,0,0.1)'
          }} data-id="element-404">
              <div className="w-8 h-8 rounded-full flex items-center justify-center" style={{
              background: 'rgba(255,255,0,0.1)',
              color: NEON
            }} data-id="element-405">
                <Check className="w-4 h-4" data-id="element-406" />
              </div>
              <span className="text-sm font-medium text-white" data-id="element-407">
                Acceso de por vida
              </span>
            </div>
            <div className="flex flex-col items-center gap-2 p-3 rounded-lg border" style={{
            background: CHARCOAL,
            borderColor: 'rgba(255,255,0,0.1)'
          }} data-id="element-408">
              <div className="w-8 h-8 rounded-full flex items-center justify-center" style={{
              background: 'rgba(255,255,0,0.1)',
              color: NEON
            }} data-id="element-409">
                <Download className="w-4 h-4" data-id="element-410" />
              </div>
              <span className="text-sm font-medium text-white" data-id="element-411">
                Acceso instantáneo
              </span>
            </div>
            <div className="flex flex-col items-center gap-2 p-3 rounded-lg border" style={{
            background: CHARCOAL,
            borderColor: 'rgba(255,255,0,0.1)'
          }} data-id="element-412">
              <div className="w-8 h-8 rounded-full flex items-center justify-center" style={{
              background: 'rgba(255,255,0,0.1)',
              color: NEON
            }} data-id="element-413">
                <Headphones className="w-4 h-4" data-id="element-414" />
              </div>
              <span className="text-sm font-medium text-white" data-id="element-415">
                Soporte técnico
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* ===== VIDEO DEMO SECTION ===== */}
      <section className="py-12 px-4 relative overflow-hidden" style={{
      background: '#0a0a0a'
    }} data-id="element-416">
        <div className="container mx-auto max-w-5xl relative z-10" data-id="element-417">
          <div className="text-center mb-10" data-reveal data-id="element-418">
            <h2 className="text-2xl md:text-4xl font-bold mb-3 uppercase text-white leading-tight" style={{
            fontFamily: "'Inter', sans-serif"
          }} data-id="element-419">
              DALE UN VISTAZO AL INTERIOR DEL MATERIAL
            </h2>
            <p className="text-sm md:text-base text-[#A1A1A1] max-w-2xl mx-auto" data-id="element-420">
              Mirá la calidad de los diagramas, planos y la estructura de los
              manuales que vas a recibir hoy mismo.
            </p>
          </div>

          {/* Video Mockup Container */}
          <div className="relative max-w-4xl mx-auto mb-12" data-reveal data-id="element-421">
            {/* Soft Glow Background */}
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[90%] h-[90%] rounded-full blur-[100px]" style={{
            background: 'rgba(255,215,0,0.08)'
          }} data-id="element-422"></div>

            {/* Mockup Frame */}
            <div className="relative rounded-xl md:rounded-2xl overflow-hidden border border-white/10 shadow-2xl bg-black" style={{
            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5), 0 0 40px rgba(255, 215, 0, 0.1)'
          }} data-id="element-423">
              {/* Mac-style top bar */}
              <div className="h-6 md:h-8 bg-[#1a1a1a] flex items-center px-3 md:px-4 border-b border-white/5" data-id="element-424">
                <div className="flex gap-1.5 md:gap-2" data-id="element-425">
                  <div className="w-2 h-2 md:w-2.5 md:h-2.5 rounded-full bg-red-500/80" data-id="element-426"></div>
                  <div className="w-2 h-2 md:w-2.5 md:h-2.5 rounded-full bg-yellow-500/80" data-id="element-427"></div>
                  <div className="w-2 h-2 md:w-2.5 md:h-2.5 rounded-full bg-green-500/80" data-id="element-428"></div>
                </div>
              </div>

              {/* Video Player */}
              <div className="relative aspect-video bg-[#121212] flex items-center justify-center group" data-id="element-429">
                <video src="https://cdn.magicpatterns.com/uploads/v6EugQ9JaUMegbvbJ31M7B/WhatsApp Video 2026-03-12 at 12.13.18.mp4" className="w-full h-full object-contain" autoPlay loop muted playsInline controls data-id="element-430" />
              </div>
            </div>
          </div>

          {/* Highlights Below Video */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-4xl mx-auto" data-reveal data-id="element-431">
            <div className="flex flex-col items-center text-center gap-3 p-4 rounded-xl bg-white/5 border border-white/5" data-id="element-432">
              <div className="w-10 h-10 rounded-full flex items-center justify-center bg-[#FFD700]/10" data-id="element-433">
                <Check className="w-5 h-5 text-[#FFD700]" data-id="element-434" />
              </div>
              <p className="text-white font-medium" data-id="element-435">
                Diagramas en alta definición
              </p>
            </div>
            <div className="flex flex-col items-center text-center gap-3 p-4 rounded-xl bg-white/5 border border-white/5" data-id="element-436">
              <div className="w-10 h-10 rounded-full flex items-center justify-center bg-[#FFD700]/10" data-id="element-437">
                <BookOpen className="w-5 h-5 text-[#FFD700]" data-id="element-438" />
              </div>
              <p className="text-white font-medium" data-id="element-439">
                Explicaciones paso a paso
              </p>
            </div>
            <div className="flex flex-col items-center text-center gap-3 p-4 rounded-xl bg-white/5 border border-white/5" data-id="element-440">
              <div className="w-10 h-10 rounded-full flex items-center justify-center bg-[#FFD700]/10" data-id="element-441">
                <Monitor className="w-5 h-5 text-[#FFD700]" data-id="element-442" />
              </div>
              <p className="text-white font-medium" data-id="element-443">
                Acceso desde cualquier dispositivo
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ===== TESTIMONIALS ===== */}
      <section className="py-8 px-4" style={{
      background: `linear-gradient(180deg, ${CHARCOAL} 0%, ${DARK} 100%)`
    }} data-id="element-444">
        <div className="container mx-auto max-w-2xl" data-reveal data-id="element-445">
          <div className="text-center mb-8" data-id="element-446">
            <div className="flex items-center justify-center gap-1 mb-2" data-id="element-447">
              {[1, 2, 3, 4, 5].map(s => <Star key={s} className="w-5 h-5 fill-amber-500 text-amber-500" data-id="element-448" />)}
            </div>
            <h2 className="text-xl md:text-2xl font-bold text-white uppercase tracking-tight leading-tight" style={{
            fontFamily: "'Inter', sans-serif"
          }} data-id="element-449">
              MÁS DE 6.500 PROFESIONALES RESPALDAN NUESTRA COMUNIDAD
            </h2>
            <p className="text-sm text-gray-500 mt-1 font-medium" data-id="element-450">
              4,9/5 — 487 reseñas verificadas
            </p>
          </div>

          {/* WhatsApp-style chat bubbles */}
          <div className="space-y-3 mb-6" data-id="element-451">
            {allChatReviews.slice(0, visibleTestimonials).map((t, i) => <TestimonialItem key={i} t={t} data-id="element-452" />)}
          </div>

          {visibleTestimonials < allChatReviews.length && <div className="text-center" data-id="element-453">
              <button onClick={showMoreTestimonials} className="btn-shine border font-medium py-3 px-8 rounded-full transition-all shadow-sm hover:shadow-md text-sm hover:bg-white/5" style={{
            background: CHARCOAL,
            borderColor: 'rgba(255,255,0,0.2)',
            color: 'rgba(255,255,255,0.8)'
          }} data-id="element-454">
                Cargar más comentarios
              </button>
            </div>}

          <ReviewForm onSubmitReview={handleSubmitReview} data-id="element-455" />
        </div>
      </section>

      {/* ===== FAQ ===== */}
      <section className="py-8 px-4" style={{
      background: DARK
    }} data-id="element-456">
        <div className="container mx-auto max-w-3xl" data-id="element-457">
          <div className="text-center mb-8" data-id="element-458">
            <h2 className="text-3xl md:text-4xl font-bold mb-4 text-white" style={{
            fontFamily: "'Montserrat', sans-serif"
          }} data-id="element-459">
              Resolvé acá tus dudas
            </h2>
            <p className="text-lg text-gray-400" data-id="element-460">
              Hacé clic para ver la respuesta que necesitás
            </p>
          </div>

          <div className="space-y-3" data-id="element-461">
            <FAQItem question="¿Qué tipo de contenido incluye OFICIO ELÉCTRICO?" answer="Incluye un manual completo de teoría y práctica eléctrica (Vol. 1 y 2), videos paso a paso, guías de normativa AEA e IRAM, diagramas técnicos y 4 bonos exclusivos con material complementario." data-id="element-462" />
            <FAQItem question="¿Necesito conocimientos previos de electricidad?" answer="No. El material está diseñado desde cero, empezando por las magnitudes eléctricas básicas y avanzando progresivamente hasta instalaciones domiciliarias completas." data-id="element-463" />
            <FAQItem question="¿Voy a aprender a hacer instalaciones reales?" answer="Sí. El foco principal es que puedas ejecutar instalaciones residenciales con criterio profesional, cumpliendo la normativa AEA 90364 vigente en Argentina." data-id="element-464" />
            <FAQItem question="¿Incluye temas de seguridad eléctrica?" answer="Sí. Cubrimos en profundidad el interruptor diferencial, la puesta a tierra, secciones de cables y protecciones térmicas para que trabajes de forma segura." data-id="element-465" />
            <FAQItem question="¿En qué formato recibo el material?" answer="Todo el contenido se entrega en formato digital con acceso inmediato a la plataforma donde podrás descargar los manuales PDF y acceder a los videos." data-id="element-466" />
            <FAQItem question="¿Tengo acceso para siempre incluyendo actualizaciones?" answer="Sí. El acceso es permanente y recibirás todas las actualizaciones futuras sin costo adicional." data-id="element-467" />
            <FAQItem question="¿Puedo usar este material para trabajar profesionalmente?" answer="Absolutamente. El material te prepara con criterio técnico real para que puedas presupuestar, ejecutar y cobrar instalaciones eléctricas de forma profesional." data-id="element-468" />
          </div>
        </div>
      </section>

      {/* ===== FOOTER ===== */}
      <footer className="text-gray-400 py-10 px-4 text-center text-sm border-t border-gray-800" style={{
      background: '#0a0a0a'
    }} data-id="element-469">
        <div className="container mx-auto" data-id="element-470">
          <div className="w-14 h-14 rounded-full overflow-hidden flex items-center justify-center mx-auto mb-6 border border-gray-800" data-id="element-471">
            <img src="https://cdn.magicpatterns.com/uploads/5J461H1BcBBEPfuB6oiBoy/Diseno_sin_titulo_(11).png" alt="Oficio Eléctrico Logo" className="w-full h-full object-cover opacity-50 grayscale" data-id="element-472" />
          </div>
          <p className="mb-2" data-id="element-473">
            &copy; {new Date().getFullYear()} OFICIO ELÉCTRICO. Todos los
            derechos reservados.
          </p>
          <p className="text-gray-600" data-id="element-474">
            Formación eléctrica profesional para futuros maestros del oficio.
          </p>
        </div>
      </footer>
    </div>;
}